import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { User, Building2, Eye, EyeOff, Sparkles, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";

const dashPath = { candidate: "/candidato", company: "/empresa", admin: "/admin" };

const ROLE_META = {
  candidate: { label: "Candidato", color: "bg-amber-100 text-amber-700", icon: User },
  company: { label: "Empresa", color: "bg-blue-100 text-blue-700", icon: Building2 },
  admin: { label: "Admin", color: "bg-slate-900 text-white", icon: ShieldCheck },
};

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState(params.get("tab") === "register" ? "register" : "login");
  const [role, setRole] = useState("candidate");
  const [form, setForm] = useState({ email: "", password: "", name: "", company_name: "" });
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [demoAccounts, setDemoAccounts] = useState(null);
  const [demoOpen, setDemoOpen] = useState(true);
  const [quickLoginEmail, setQuickLoginEmail] = useState(null);
  const next = params.get("next");
  const refCode = params.get("ref");

  useEffect(() => {
    if (mode !== "login") return;
    api.get("/demo/accounts")
      .then((r) => setDemoAccounts(r.data))
      .catch(() => setDemoAccounts({ enabled: false, accounts: [] }));
  }, [mode]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const doLogin = async (email, password) => {
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Bem-vindo de volta!");
      navigate(next || dashPath[user.role] || "/");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
      setQuickLoginEmail(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "login") {
      await doLogin(form.email, form.password);
    } else {
      setLoading(true);
      try {
        const user = await register({ ...form, role, gdpr_consent: gdpr, referral_code: refCode || undefined });
        toast.success("Conta criada com sucesso!");
        navigate(next || dashPath[user.role] || "/");
      } catch (err) {
        toast.error(formatErr(err));
      } finally { setLoading(false); }
    }
  };

  const quickLogin = async (account) => {
    setQuickLoginEmail(account.email);
    const pwd = account.password || demoAccounts?.password || "Demo@2026";
    setForm({ ...form, email: account.email, password: pwd });
    await doLogin(account.email, pwd);
  };

  const showDemoPanel = mode === "login" && demoAccounts?.enabled && demoAccounts.accounts?.length > 0;

  // Feedback do fluxo Google OAuth (?google=1 sucesso é tratado no dashboard, ?google_error= aqui)
  useEffect(() => {
    const err = params.get("google_error");
    if (err) {
      const msgs = {
        not_configured: "Login com Google ainda não está configurado.",
        state_mismatch: "Sessão de autenticação expirou. Tenta novamente.",
        not_candidate: "Este email já está registado como empresa/admin.",
        email_unverified: "O teu email Google não está verificado.",
      };
      toast.error(msgs[err] || "Falha no login com Google. Tenta novamente.");
    }
  }, [params]);

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/start`;
  };
  const showGoogleBtn = mode === "login" || role === "candidate";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-md mx-auto px-4 py-16">
        <h1 className="font-heading font-black tracking-tighter text-3xl text-slate-900 mb-2">
          {mode === "login" ? "Entrar" : "Criar conta"}
        </h1>
        <p className="text-sm text-slate-500 mb-8">
          {mode === "login" ? "Acede ao teu painel." : "Gratuito para candidatos e empresas."}
        </p>
        {mode === "register" && refCode && (
          <p data-testid="referral-invite-note" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
            Foste convidado/a — código de convite <b>{refCode}</b> aplicado.
          </p>
        )}

        {/* Painel de contas demo (só aparece se seed correu) */}
        {showDemoPanel && (
          <div
            data-testid="demo-accounts-panel"
            className="bg-gradient-to-br from-amber-50 to-white border border-amber-200 rounded-2xl p-4 mb-4">
            <button
              type="button"
              onClick={() => setDemoOpen((v) => !v)}
              className="w-full flex items-center justify-between text-left mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-600" />
                <span className="font-heading font-bold tracking-tight text-sm text-slate-900">
                  Contas de demonstração
                </span>
              </div>
              {demoOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            {demoOpen && (
              <>
                <p className="text-xs text-slate-600 mb-3">
                  Toca numa conta para entrar direto — ideal para testar sem preencher.
                  Password: <code className="bg-white/60 px-1.5 py-0.5 rounded text-[11px] font-mono">{demoAccounts.password}</code>
                </p>
                <div className="grid gap-2">
                  {demoAccounts.accounts.map((a) => {
                    const meta = ROLE_META[a.role] || ROLE_META.candidate;
                    const Icon = meta.icon;
                    const isBusy = quickLoginEmail === a.email && loading;
                    return (
                      <button
                        key={a.email}
                        type="button"
                        data-testid={`demo-${a.role}-${a.email.split("@")[0]}`}
                        onClick={() => quickLogin(a)}
                        disabled={loading}
                        className="w-full flex items-center gap-3 bg-white rounded-xl border border-black/5 p-3 hover:border-amber-300 hover:shadow-sm transition text-left disabled:opacity-60 disabled:cursor-not-allowed">
                        <div className={`w-9 h-9 rounded-full ${meta.color} flex items-center justify-center shrink-0`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="font-semibold text-slate-900 text-sm truncate">{a.label}</p>
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${meta.color}`}>
                              {meta.label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{a.description}</p>
                        </div>
                        {isBusy && (
                          <span className="text-xs text-amber-600 font-semibold shrink-0">A entrar…</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-6 sm:p-8">
          {mode === "register" && (
            <Tabs value={role} onValueChange={setRole} className="mb-6">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger data-testid="tab-candidato" value="candidate"><User className="w-4 h-4 mr-1.5" />Candidato</TabsTrigger>
                <TabsTrigger data-testid="tab-empresa" value="company"><Building2 className="w-4 h-4 mr-1.5" />Empresa</TabsTrigger>
              </TabsList>
              <TabsContent value="company">
                <p className="text-xs text-slate-500 mt-2">O perfil da empresa passa por validação da equipa FIRST2JOB.</p>
              </TabsContent>
            </Tabs>
          )}
          {showGoogleBtn && (
            <>
              <button
                type="button"
                data-testid="auth-google-btn"
                onClick={startGoogleLogin}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 h-11 text-sm font-semibold text-slate-700 shadow-sm transition disabled:opacity-60"
              >
                <svg className="w-4 h-4" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/>
                  <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.6 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                  <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.3 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
                  <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.6l6.2 5.2C41.4 35 44 30 44 24c0-1.2-.1-2.3-.4-3.5z"/>
                </svg>
                {mode === "login" ? "Entrar com Google" : "Registar com Google"}
              </button>
              <div className="flex items-center gap-3 my-5 text-[10px] uppercase tracking-widest text-slate-400 font-semibold">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span>ou com email</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
            </>
          )}
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="mb-1.5 block">{role === "company" ? "Nome do responsável" : "Nome completo"}</Label>
                <Input data-testid="auth-name-input" required value={form.name} onChange={set("name")} placeholder="O teu nome" />
              </div>
            )}
            {mode === "register" && role === "company" && (
              <div>
                <Label className="mb-1.5 block">Nome da empresa</Label>
                <Input data-testid="auth-company-input" required value={form.company_name} onChange={set("company_name")} placeholder="Empresa, Lda." />
              </div>
            )}
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input data-testid="auth-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="email@exemplo.pt" />
            </div>
            <div>
              <Label className="mb-1.5 block">Password</Label>
              <div className="relative">
                <Input
                  data-testid="auth-password-input"
                  required
                  type={showPassword ? "text" : "password"}
                  minLength={6}
                  value={form.password}
                  onChange={set("password")}
                  placeholder="••••••••"
                  className="pr-11"
                />
                <button
                  type="button"
                  data-testid="auth-toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Ocultar password" : "Mostrar password"}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center justify-center w-11 text-slate-400 hover:text-slate-700 transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {mode === "register" && (
              <label className="flex items-start gap-2.5 text-xs text-slate-600 cursor-pointer">
                <Checkbox data-testid="auth-gdpr-checkbox" checked={gdpr} onCheckedChange={setGdpr} className="mt-0.5" />
                <span>
                  Aceito os{" "}
                  <a data-testid="auth-terms-link" href="/legal/termos-e-condicoes" target="_blank" rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="font-semibold text-slate-900 underline decoration-amber-400 decoration-2 underline-offset-2">
                    Termos e Condições
                  </a>{" "}
                  e a{" "}
                  <a data-testid="auth-privacy-link" href="/legal/politica-de-privacidade" target="_blank" rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="font-semibold text-slate-900 underline decoration-amber-400 decoration-2 underline-offset-2">
                    Política de Privacidade
                  </a>{" "}
                  e o tratamento dos meus dados nos termos do RGPD.
                </span>
              </label>
            )}
            <Button data-testid="auth-submit-btn" type="submit" disabled={loading || (mode === "register" && !gdpr)}
              className="w-full rounded-full bg-slate-900 hover:bg-slate-800 h-11">
              {loading ? "Aguarda..." : mode === "login" ? "Entrar" : "Criar conta"}
            </Button>
          </form>
          <button data-testid="auth-switch-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="text-sm text-slate-500 hover:text-slate-900 transition-colors mt-5 block mx-auto">
            {mode === "login" ? "Ainda não tens conta? Regista-te" : "Já tens conta? Entrar"}
          </button>
        </div>
      </div>
    </div>
  );
}
